import React, { useState } from 'react';
import { Button, ActionSheet, Toast } from 'antd-mobile';
import { Action } from 'antd-mobile/es/components/action-sheet';
import { t } from 'i18n';
import PickUpPhotoService from "../services/PickUpPhotoService";
import {isPickUpNativeBridgeAvailable, PickUpImageCompressionOptions} from "./PickUpPhotoNativeBridge";

// 简单的平台检测函数
const isIOS = (): boolean => {
  return window?.webkit?.messageHandlers !== undefined;
};

interface PickUpPhotoSelectorProps {
  onPhotoSelected: (file: File) => void;
  buttonText?: string | React.ReactNode;
  buttonProps?: any;
  children?: React.ReactNode;
  showActionSheet?: boolean;
  disabled?: boolean;
  compressionOptions?: PickUpImageCompressionOptions; // 图片压缩选项
}

const PickUpPhotoSelector: React.FC<PickUpPhotoSelectorProps> = ({
  onPhotoSelected,
  buttonText,
  buttonProps,
  showActionSheet,
  disabled = false,
  compressionOptions,
}) => {
  const defaultShowActionSheet = showActionSheet ?? (isIOS() ? false : true);
  const [loading, setLoading] = useState(false);
  const [actionSheetVisible, setActionSheetVisible] = useState(false);

  const handleTakePhoto = async (useFrontCamera: boolean = false) => {
    setLoading(true);
    try {
       const file = await PickUpPhotoService.takePhoto(useFrontCamera, compressionOptions);
      onPhotoSelected(file);
    } catch (error) {
      console.error('Photo capture failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      Toast.show({ content: t('pages.pickUp.photo.captureFailed', { errorMessage }), position: 'bottom' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFromGallery = async () => {
    setLoading(true);
    try {
      const file = await PickUpPhotoService.selectFromGallery(compressionOptions);
      onPhotoSelected(file);
    } catch (error) {
      console.error('Photo selection failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      Toast.show({ 
        content: t('pages.pickUp.photo.selectionFailed', { errorMessage }), 
        position: 'bottom' 
      });
    } finally {
      setLoading(false);
    }
  };

  const showPhotoOptions = () => {
    if (defaultShowActionSheet) {
      setActionSheetVisible(true);
    } else {
      // 如果不显示ActionSheet，默认从相册选择
      handleSelectFromGallery();
    }
  };

  // 根据是否有原生桥接来决定显示哪些选项
  const getActions = (): Action[] => {
    const actions: Action[] = [];

    console.log('isNativeBridgeAvailable', isPickUpNativeBridgeAvailable());

    if (isPickUpNativeBridgeAvailable()) {
      actions.push(
        {
          text: t('pages.delivery.detail.completeTakePhoto'),
          key: 'camera',
          onClick: () => {
            setActionSheetVisible(false);
            handleTakePhoto(false);
          },
        },
      );
    }

    actions.push(
      {
        text: t('pages.delivery.detail.completeTakeSelect'),
        key: 'gallery',
        onClick: () => {
          setActionSheetVisible(false);
          handleSelectFromGallery();
        },
      },
      {
        text: t('pages.delivery.detail.cancel'),
        key: 'cancel',
        onClick: () => {
          setActionSheetVisible(false);
        },
      }
    );

    return actions;
  };

  return (
    <>
      <Button
        onClick={showPhotoOptions}
        loading={loading}
        block
        fill="none"
        disabled={disabled}
        {...buttonProps}
      >
        {buttonText}
      </Button>
      {defaultShowActionSheet && (
        <ActionSheet
          visible={actionSheetVisible}
          actions={getActions()}
          onClose={() => setActionSheetVisible(false)}
        />
      )}
    </>
  );
};

export default PickUpPhotoSelector;